'use client';
import React from 'react';
import PropTypes from 'prop-types';
import Base from '../../layouts/Base';
import s from './ArchivePage.module.scss';
import CategoryHeader from '../../components/CategoryHeader';
import ListZeroState from '../../components/ListZeroState';
import ReportItem from '../../components/ReportItem/ReportItem';
import { t } from '../../i18n';
import ReportsFilter from '../../components/ReportsFilter/ReportsFilter';
import { useSearchParams } from 'next/navigation';
import { rememberReportOrigin } from '../../utils/reportReturn';
import classNames from 'classnames';
import { OTHER_CATEGORY_ID } from '../ReportsPage/groupReportsByCategory';

export const reportsUrl = (rootUrl) =>
    `${(rootUrl || '').replace(/\/+$/, '')}/`;
export const archiveUrl = (rootUrl) => `${reportsUrl(rootUrl)}archive/`;

const ArchivePage = ({
    rootUrl,
    tags = [],
    authors = [],
    countries = [],
    searchCategories = [],
    searchResults = [],
}) => {
    const searchParams = useSearchParams();
    const hasQuery = searchParams.toString().length > 0;

    const otherCategory = {
        id: OTHER_CATEGORY_ID,
        label: t('reportsPage.otherReports'),
    };

    const keyword = searchParams.get('q') || '';
    const searchWords = keyword ? [keyword] : [];
    const activeCategories = (searchParams.get('categories') || '')
        .split(',')
        .filter(Boolean);
    const urlWith = (mutate) => {
        const params = new URLSearchParams(searchParams.toString());
        mutate(params);
        const query = params.toString();
        return query ? `${archiveUrl(rootUrl)}?${query}` : archiveUrl(rootUrl);
    };

    const categoryHref = (id) =>
        urlWith((params) => {
            const key = String(id);
            const next = activeCategories.includes(key)
                ? activeCategories.filter((value) => value !== key)
                : [...activeCategories, key];

            params.delete('categories');
            if (next.length) {
                params.set('categories', next.join(','));
            }
        });

    return (
        <div className={s.Root}>
            <main className={s.Main}>
                <div className={s.Toolbar}>
                    <ReportsFilter
                        tags={tags}
                        authors={authors}
                        countries={countries}
                        rootUrl={rootUrl}
                        onQueryLoadingChange={() => {}}
                    />
                    {hasQuery && (
                        <a className={s.ClearAll} href={reportsUrl(rootUrl)}>
                            {t('archivePage.clearAll')}
                        </a>
                    )}
                </div>

                <div className={s.Summary}>
                    <p className={s.SummaryText}>
                        <span>{t('archivePage.weFound')}</span>
                        <span className={s.Count}>{searchResults.length}</span>
                        <span>{t('archivePage.resultsMatching')}</span>
                    </p>
                </div>

                <section className={s.Reports}>
                    <CategoryHeader
                        label={t('reportsPage.reports')}
                        count={searchResults.length}
                        showPin={false}
                        showToggle={false}
                        modifiers={['Plain']}
                    />
                    <div className={s.Body}>
                        {!!searchCategories.length && (
                            <div className={s.Categories}>
                                <span className={s.CategoriesLabel}>
                                    {t('archivePage.filterCategories')}
                                </span>
                                <div className={s.Chips}>
                                    {searchCategories.map((category) => (
                                        <a
                                            key={category.id}
                                            href={categoryHref(category.id)}
                                            className={classNames(s.Chip, {
                                                [s.ChipActive]:
                                                    activeCategories.includes(
                                                        String(category.id)
                                                    ),
                                            })}>
                                            {category.id === OTHER_CATEGORY_ID
                                                ? otherCategory.label
                                                : category.label}
                                        </a>
                                    ))}
                                </div>
                            </div>
                        )}
                        <div
                            className={s.List}
                            onClickCapture={(event) => {
                                const link = event.target.closest('a');

                                if (link) {
                                    rememberReportOrigin(link.pathname);
                                }
                            }}>
                            {searchResults.map((report, index) => (
                                <ReportItem
                                    key={report.id || index}
                                    {...report}
                                    category={report.category || otherCategory}
                                    isLarge
                                    showTags
                                    modifiers={['Card', 'Result']}
                                    searchWords={searchWords}
                                />
                            ))}
                            {!searchResults.length && (
                                <ListZeroState
                                    title={t('archivePage.noReports')}
                                    text={t('archivePage.zeroStateHint')}
                                />
                            )}
                        </div>
                    </div>
                </section>
            </main>
        </div>
    );
};

ArchivePage.propTypes = {
    rootUrl: PropTypes.string,
    tags: PropTypes.array,
    authors: PropTypes.array,
    countries: PropTypes.array,
    searchCategories: PropTypes.array,
    searchResults: PropTypes.array,
};

export const RawArchivePage = ArchivePage;
export default Base(ArchivePage);
